import { Injectable } from '@angular/core';
import { ApiService } from '../api/api.service';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { VariableConfig } from '../../interfaces/misc/variable-config';
import { Subscription } from 'rxjs';
import { OfflineService } from '../offline/offline.service';
import { IdbService } from '../idb/idb.service';
import { RoleService } from '../role/role.service';
import * as Sentry from '@sentry/angular';
import { DatePipe } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class ConfigurationService {
  connectionStatusSubscription: Subscription;
  connectionStatus: boolean;

  constructor(
    private api: ApiService,
    private local: LocalStorageService,
    private offline: OfflineService,
    private idb: IdbService,
    private role: RoleService,
    public datePipe: DatePipe
  ) {
    this.connectionStatusSubscription =
      this.offline.currentConnectionStatus.subscribe((connectionStatus) => {
        this.connectionStatus = connectionStatus;
      });
  }

  getVariableConfiguration() {
    this.api.getVariableConfig().subscribe({
      next: (config: VariableConfig) => {
        if (config) {
          // Store each key/value pair in local storage
          Object.keys(config).forEach((key) => {
            const value = config[key];
            this.local.store(key, JSON.stringify(value));
          });
          // Store the last time the configuration was updated
          const currentTime = new Date().getTime();
          try {
            localStorage.setItem(
              'last_config_update_time',
              JSON.stringify(currentTime)
            );
          } catch (error) {
            Sentry.captureEvent({
              message: `Error storing data in localStorage: ${error}`,
              level: 'error',
              tags: {
                date: this.datePipe.transform(new Date(), 'yyyy-MM-dd'),
                user: this.local.get('name'),
              },
            });
          }
        }
      },
      error: (error) => {
        console.error('Error fetching configuration:', error);
      },
    });
  }

  checkUserRole() {
    let userId = this.local.get('uid');
    this.api.getUserData(userId).subscribe({
      next: (user) => {
        if (user.length > 0) {
          let currentUserRole = this.local.get('role');
          if (currentUserRole !== user[0].userRoleName) {
            this.idb.updateUser(user[0]).subscribe();
            this.role.updateUserRole();
            this.local.store('role', user[0].userRoleName);
            console.log('user role updated:', user[0].userRoleName);
            setTimeout(() => {
              window.location.reload();
            }, 5000);
          }
        }
      },
      error: (error) => {
        console.error('Error fetching user role:', error);
      },
    });
  }

  // Background task which checks the last time the configuration was updated
  // If it's been more than checkIfUpdateNeededInterval hours, it will update the configuration
  // If the user is offline, it will retry at regular intervals
  startBackgroundTask(checkIfUpdateNeededInterval: number) {
    // Set the interval for the background task
    setInterval(() => {
      if (this.connectionStatus) {
        this.checkAndUpdateConfiguration();
      } else {
        this.handleOfflineScenario();
      }
    }, checkIfUpdateNeededInterval);
  }

  private checkAndUpdateConfiguration() {
    // Might need to convert this to a number
    // Doesn't seem to be necessary
    const lastUpdateTime = JSON.parse(
      localStorage.getItem('last_config_update_time') || '0'
    );
    const currentTime = new Date().getTime();
    // Need to update in app component too if we change it here
    const updateInterval = 1000 * 60 * 60; // 1  hour in milliseconds

    // Check if it has been more than the update interval since the last update
    console.log(currentTime - lastUpdateTime, updateInterval);
    if (currentTime - lastUpdateTime >= updateInterval) {
      this.getVariableConfiguration();
      this.checkUserRole();
    }
  }

  handleOfflineScenario() {
    const retryInterval = 60000; // 60 seconds in milliseconds
    let retryIntervalId: any; // Variable to store the interval ID

    // Listen for the 'online' event and retry updating the configuration
    window.addEventListener('online', () => {
      this.checkAndUpdateConfiguration();
    });

    // Retry at regular intervals if the user is offline
    // Once the user is connected, the interval will be cleared
    retryIntervalId = setInterval(() => {
      if (navigator.onLine) {
        this.checkAndUpdateConfiguration();
        clearInterval(retryIntervalId); // Clear the interval when the user is connected again
      }
    }, retryInterval);
  }

  ngOnDestroy() {
    this.connectionStatusSubscription.unsubscribe();
  }
}
